import React, { useMemo, type ReactNode, type ComponentType } from 'react';
import { createHtmlPortalNode, InPortal, OutPortal } from 'react-reverse-portal';
import { UNSAFE_LAYERING } from '@atlaskit/layering';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';

export type ReParenterProps = {
	children?: ReactNode;
	Parent: ComponentType<{
		children?: ReactNode;
	}>;
};

/**
 * When you change the parent of a component (reparent) this changes the React tree and thus causes a remount of the children
 * This causes state of children to be lost, as well as causing us to incur the cost of remounting the child
 * which can be very expensive and cause performance issues
 *
 * This component uses react-reverse-portal to allow us to reparent without remounting
 * therefore bypassing state loss and performance concerns
 */

export const ReParenter = ({ children, Parent }: ReParenterProps) => {
	const portalNode = useMemo(() => createHtmlPortalNode(), []);
	return (
		<>
			<InPortal node={portalNode}>
				{/* See more details about UNSAFE_LAYERING https://hello.atlassian.net/wiki/spaces/~63ff0a61f00d095406f28492/pages/2984762483/Layering+package+introduction */}
				<UNSAFE_LAYERING
					isDisabled={
						getBooleanFF('platform.design-system-team.layering_qmiw3') ? false : undefined
					}
				>
					{children}
				</UNSAFE_LAYERING>
			</InPortal>
			<Parent>
				<OutPortal node={portalNode} />
			</Parent>
		</>
	);
};
